/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable indent */
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, of, share, tap } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { DataItem } from '@shared/models/data-item';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { DecsService } from '@shared/services/decs/decs.service';
import { DecsResponse } from '@shared/services/decs/models/response/decs.response';
import { CrewStore } from '../crew/crew.store';
import { CrewRequest } from '../crew/models/request/crew.request';
import { CrewListView } from '../crew/models/view/crew-list.view';
import { FlightNumberAndDate } from './models/flight-and-date';
import { InboundFlightView } from './models/inbound-flight.view';
import { NsAirports } from './models/ns-airports';
import { LegView } from '@sequence/models/view/leg.view';
import { CrewService } from '@flight-details/crew/crew.service';
import { CrewListResponse } from '@flight-details/crew/models/response/crew-list.response';

@Injectable({
  providedIn: 'root',
})
export class InboundFlightStore {
  constructor(
    private crewService: CrewService,
    private crewStore: CrewStore,
    private decsService: DecsService,
    private alertService: AlertService
  ) {}

  /**
   * Returns the inbound flight's crew.
   * This actually requires quite a few steps:
   *  - Determine correct full date by comparing NS (which only contains day number) and current flight's date to create
   *    assumed full date.
   *  - Get inbound flight generic NS (crew list, but requested without dep airport because it's unknown at this point).
   *  - Parse dep and arr airports from this NS.
   *  - Determine the correct dep airport to use by comparing selected flight dep airport and inbound dep/arr airports.
   *  - Get crew list from API with data provided from above.
   */
  getInboundCrewList(
    legView: LegView,
    isRefresh = false
  ): Observable<RequestState<CrewListView>> {
    // This needs to be used later in the pipeline and it was difficult to pass it through.
    let inboundFlightNumAndDate: RequestState<FlightNumberAndDate>;

    // Start by getting crew list.
    return this.getInboundFlightInfo(legView, isRefresh).pipe(
      map((inboundFlightInfo) =>
        this.getInboundFlightNumAndDate(inboundFlightInfo, legView)
      ),
      tap((inboundFltNumAndDate) => {
        // Set for use after getting NS.
        inboundFlightNumAndDate = inboundFltNumAndDate;
      }),
      switchMap((inboundFltNumAndDate) =>
        this.getInboundNs(inboundFltNumAndDate, isRefresh)
      ),
      map((inboundNs) =>
        this.getInboundCrewRequest(inboundNs, inboundFlightNumAndDate, legView)
      ),
      switchMap((inboundCrewRequest) =>
        this.getInboundCrew(inboundCrewRequest, isRefresh)
      )
    );
  }

  /** Returns data item array to display inbound flight status. */
  getInboundFlightStatus(
    legView: LegView,
    isRefresh = false
  ): Observable<RequestState<InboundFlightView>> {
    const crewRequest = legView.getCrewRequest();

    if (!crewRequest) {
      return of(
        new RequestState<InboundFlightView>(RequestStatus.IsCompleteNoResponse)
      );
    }

    return this.getInboundFlightInfo(legView, isRefresh).pipe(
      map((inboundFlightInfo) => this.mapDataItems(inboundFlightInfo))
    );
  }

  /** Get crew list and map to InboundFlightView. Multiple observables rely on this one, so the result is shared. */
  private getInboundFlightInfo(
    legView: LegView,
    isRefresh: boolean
  ): Observable<RequestState<InboundFlightView>> {
    const crewRequest = legView.getCrewRequest();

    if (!crewRequest) {
      return of(
        new RequestState<InboundFlightView>(RequestStatus.IsCompleteNoResponse)
      );
    }

    return this.crewService.getCrewList(crewRequest, isRefresh).pipe(
      map((crewList) => this.mapInboundFlightInfo(crewList)),
      share()
    );
  }

  /** Parse and return the inbound flight information from the current flight crew list. */
  private mapInboundFlightInfo(
    crewListState: RequestState<CrewListResponse>
  ): RequestState<InboundFlightView> {
    if (!crewListState.response) {
      return crewListState.into<InboundFlightView>();
    }

    const inboundFlight = this.parseInboundFlight(
      crewListState.response.timestamp,
      crewListState.response?.gate?.raw
    );

    if (!inboundFlight) {
      return crewListState.into<InboundFlightView>();
    }

    return crewListState.into<InboundFlightView>(inboundFlight);
  }

  /** Extract flight number and determine date of inbound flight.  */
  private getInboundFlightNumAndDate(
    inboundFlightInfo: RequestState<InboundFlightView>,
    legView: LegView
  ): RequestState<FlightNumberAndDate> {
    if (!inboundFlightInfo.response) {
      return inboundFlightInfo.into<FlightNumberAndDate>();
    }

    const date = this.getInboundFlightDate(inboundFlightInfo.response, legView);

    if (!date) {
      return inboundFlightInfo.into<FlightNumberAndDate>();
    }

    const flightNumAndDate: FlightNumberAndDate = {
      flightNumber: inboundFlightInfo.response?.flightNumber || '',
      date: date,
    };

    return inboundFlightInfo.into<FlightNumberAndDate>(flightNumAndDate);
  }

  /**
   * Determine the inbound flight departure date. The inbound flight information only provides
   * a day, so assumptions must be made to construct a full date.
   */
  private getInboundFlightDate(
    inboundFlight: InboundFlightView,
    legView: LegView
  ): DateTime | null {
    const selectedFlightDate = legView.depTimeSched;

    if (!inboundFlight.flightDay || !selectedFlightDate?.day) {
      return null;
    }

    const inboundFlightDay = parseInt(inboundFlight.flightDay);
    const selectedFlightDay = selectedFlightDate.day;

    let inboundMonth = selectedFlightDate.month;

    // Get delta between inbound and selected flight. If a large difference exists, it is assumed that the day is from the
    // previous month. Ex: flightDay is 1, inboundFlightDay is 30.
    const delta = Math.abs(selectedFlightDay - inboundFlightDay);

    if (delta > 20) {
      if (inboundMonth > 1) {
        inboundMonth -= 1;
      } else {
        inboundMonth = 12;
      }
    }

    // Create new date based on selectedFlightDate and inboundFlightDay.
    const inboundFlightDate = DateTime.fromObject({
      year: selectedFlightDate.year,
      month: inboundMonth,
      day: inboundFlightDay,
    });

    // If invalid date created, return null.
    if (inboundFlightDate.invalidReason) {
      return null;
    }

    return inboundFlightDate;
  }

  /**
   * Get the NS Sabre text of the inbound flight. We don't use the normal crew API
   * for this request because we don't know the departure airport yet. The version
   * of the NS command used here only needs flight number and date. This step is
   * used to figure out the departure airport.
   */
  private getInboundNs(
    flightNumAndDate: RequestState<FlightNumberAndDate>,
    isRefresh: boolean
  ): Observable<RequestState<DecsResponse>> {
    if (!flightNumAndDate.response) {
      return of(flightNumAndDate.into<DecsResponse>());
    }

    const { flightNumber, date } = flightNumAndDate.response;

    if (!flightNumber || !date) {
      return of(flightNumAndDate.into<DecsResponse>());
    }

    return this.decsService
      .submitDecsRequest(
        {
          cmd: `NS/${flightNumber}/${date.toFormat('ddMMM')}`,
        },
        isRefresh
      )
      .pipe(
        map((response) => {
          if (response.hasError) {
            this.showErrorAlert();
          }
          return response;
        })
      );
  }

  /** Create crew request for inbound flight. */
  private getInboundCrewRequest(
    inboundNsState: RequestState<DecsResponse>,
    inboundFlightNumAndDate: RequestState<FlightNumberAndDate>,
    legView: LegView
  ): RequestState<CrewRequest> {
    if (!inboundNsState.response || !inboundFlightNumAndDate.response) {
      return inboundNsState.into<CrewRequest>();
    }

    const inboundNs = inboundNsState.response;
    const inboundFltNumAndDate = inboundFlightNumAndDate.response;
    const airports = this.parseNsDepArrivalArpts(inboundNs.result);

    if (!airports) {
      return inboundNsState.into<CrewRequest>();
    }

    const inboundFlightDepAirport = this.getInboundFlightDepAirport(
      airports,
      legView
    );

    const inboundCrewRequest: CrewRequest = {
      flightNumber: inboundFltNumAndDate.flightNumber || '',
      date: inboundFltNumAndDate.date.toISODate() || '',
      depStation: inboundFlightDepAirport,
    };

    return inboundNsState.into<CrewRequest>(inboundCrewRequest);
  }

  /** Determine the correct departure airport of the inbound flight.  */
  private getInboundFlightDepAirport(
    inboundFlightAirports: NsAirports,
    selectedFlight: LegView
  ): string {
    if (!inboundFlightAirports || !selectedFlight) {
      return '';
    }

    // Check if inbound arr airport matches selected flight dep airport. If so,
    // use inbound dep airport for request. If not, use inbound arr airport as
    // dep airport for request.
    let depAirport = inboundFlightAirports.departure;

    if (selectedFlight.depStation !== inboundFlightAirports.arrival) {
      depAirport = inboundFlightAirports.arrival;
    }

    if (!depAirport) {
      return '';
    }

    return depAirport;
  }

  /** Make API request for inbound crew list. */
  private getInboundCrew(
    inboundCrewRequest: RequestState<CrewRequest>,
    isRefresh: boolean
  ): Observable<RequestState<CrewListView>> {
    if (!inboundCrewRequest.response) {
      return of(inboundCrewRequest.into<CrewListView>());
    }

    return this.crewStore.getCrew(inboundCrewRequest.response, isRefresh);
  }

  /**
   * Extract the inbound flight data from the raw NS text.
   * Example: 2118/11 92 N825  FROM 2118/11 IN 1256/11 AS OF 13FEB23/1439
   */
  private parseInboundFlight(
    timestamp: string,
    text?: string
  ): InboundFlightView | null {
    if (text && text.length > 0) {
      const firstLine = text.split('\n')[0];
      const pattern =
        /FROM\s+(UNKWN|(?<flightNum>\d{1,4})\/(?<flightDay>\d{2})\s+(?<status>[A-Z]{2,3})\s+(?<arrTime>\d{4})\/(?<arrDay>\d{2}))/;
      const match = firstLine.match(pattern);

      if (match) {
        if (match[1] === 'UNKWN') {
          return {
            timestamp: DateTime.fromISO(timestamp, { zone: 'utc' }),
            status: match[1],
          };
        } else {
          return {
            timestamp: DateTime.fromISO(timestamp, { zone: 'utc' }),
            flightNumber: match.groups?.['flightNum'],
            flightDay: match.groups?.['flightDay'],
            status: match.groups?.['status'] || '',
            arrivalTime: match.groups?.['arrTime'],
            arrivalDay: match.groups?.['arrDay'],
          };
        }
      }
    }

    return null;
  }

  /** Parse the departure and arrival airports from the NS text. Returns null on failure. */
  // eslint-disable-next-line complexity
  private parseNsDepArrivalArpts(sabreText?: string): NsAirports | null {
    if (!sabreText) {
      return null;
    }

    const lines = sabreText.split('\n');

    if (lines.length < 3) {
      return null;
    }

    const depPattern = /^DEP (?<dep>[A-Z]{3})/;
    const arrPattern = /^ARV (?<arr>[A-Z]{3})/;
    const airports: NsAirports = {
      departure: '',
      arrival: '',
    };

    for (const line of lines) {
      const depMatch = line.match(depPattern);
      const arrMatch = line.match(arrPattern);

      if (depMatch) {
        airports.departure = depMatch.groups?.['dep'] || '';
      } else if (arrMatch) {
        airports.arrival = arrMatch.groups?.['arr'] || '';
      }
    }

    if (airports.departure === '' || airports.arrival === '') {
      return null;
    }

    return airports;
  }

  /** Create DataItem array for display in DataBox. */
  // eslint-disable-next-line complexity
  private mapDataItems(
    inboundFlight: RequestState<InboundFlightView>
  ): RequestState<InboundFlightView> {
    if (!inboundFlight.response) {
      return inboundFlight;
    }

    const { status, flightNumber, arrivalDay, arrivalTime } =
      inboundFlight.response;

    // Change status to be more descriptive.
    let descriptiveStatus = '';
    let statusStyle: 'warning' | 'caution' | 'success' | 'bold' | undefined;

    if (status === 'UNKWN') {
      descriptiveStatus = 'Unknown';
      statusStyle = 'caution';
    } else if (status === 'SKD') {
      descriptiveStatus = 'Scheduled';
      statusStyle = 'caution';
    } else if (status === 'ETA') {
      descriptiveStatus = 'Enroute';
      statusStyle = 'caution';
    } else if (status === 'ON') {
      descriptiveStatus = 'On Ground';
      statusStyle = 'success';
    } else if (status === 'IN') {
      descriptiveStatus = 'Arrived';
      statusStyle = 'success';
    } else {
      descriptiveStatus = '';
    }

    // Format arrival time.
    let formattedArrivalTime = '';
    if (arrivalDay && arrivalTime && arrivalTime.length === 4) {
      const hours = arrivalTime.substring(0, 2);
      const min = arrivalTime.substring(2);
      formattedArrivalTime = `${hours}:${min}`;
    }

    const dataItems: DataItem[] = [];

    if (flightNumber && formattedArrivalTime) {
      dataItems.push(
        {
          label: 'Flight',
          value: flightNumber || '',
        },
        {
          label: 'Status',
          value: descriptiveStatus,
          style: statusStyle,
        },
        {
          label: 'Arrival Time',
          value: formattedArrivalTime,
        }
      );
    } else {
      // Omit flight and arrival time if they are blank.
      dataItems.push({
        label: 'Status',
        value: descriptiveStatus,
        style: statusStyle,
      });
    }

    return inboundFlight.into<InboundFlightView>({
      ...inboundFlight.response,
      dataItems,
    });
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Failed to load inbound flight data.',
    });
  }
}
